/**
 * WorkflowTemplateService
 *
 * This service handles all workflow template-related operations in the application.
 * It provides methods for managing workflow templates, including creation, application
 * to projects, and milestone updates. The service supports operations for both
 * template management and milestone lifecycle tracking with audit trail capabilities.
 *
 * @class
 */

import HttpService from './HttpService';
import {
  pathWorkflowTemplates,
  pathWorkflowTemplateId,
  pathWorkflowTemplatesApply,
  pathWorkflowMilestoneId,
  pathWorkflowScheduleAggregates,
  pathWorkflowOverdueDetectionJob,
} from '@frontend/__settings__/septSoftwarePaths';
import type {
  OverdueDetectionSummary,
  ScheduleAggregateRequestParams,
  ScheduleAggregateResponseData,
  WorkflowGenericResponse,
  WorkflowMilestoneUpdatePayload,
  WorkflowMilestoneUpdateResponseData,
  WorkflowTemplateApplyPayload,
  WorkflowTemplateApplyResponseData,
  WorkflowTemplatePayload,
  WorkflowTemplateResponseData,
  TemplateListFilters,
  TemplateListResponse,
} from '@frontend/types/workflows';

const http = new HttpService();

class WorkflowTemplateService {
  /**
   * List workflow templates for the current tenant with optional filters/pagination.
   */
  static async listWorkflowTemplates(
    filters?: TemplateListFilters
  ): Promise<WorkflowGenericResponse<TemplateListResponse>> {
    return http.get<WorkflowGenericResponse<TemplateListResponse>>(
      pathWorkflowTemplates,
      { params: filters }
    );
  }

  /**
   * Fetch a single workflow template with steps.
   */
  static async getWorkflowTemplate(
    templateId: string
  ): Promise<WorkflowGenericResponse<WorkflowTemplateResponseData>> {
    return http.get<WorkflowGenericResponse<WorkflowTemplateResponseData>>(
      pathWorkflowTemplateId(templateId)
    );
  }

  /**
   * Creates a new workflow template with ordered steps for the authenticated tenant.
   *
   * @param {WorkflowTemplatePayload} templateData - Template data including name, category, description, and steps
   * @returns {Promise<WorkflowGenericResponse<WorkflowTemplateResponseData>>} Created template object with steps
   * @throws {Error} If the API request fails
   */
  static async createWorkflowTemplate(
    templateData: WorkflowTemplatePayload
  ): Promise<WorkflowGenericResponse<WorkflowTemplateResponseData>> {
    return http.post<
      WorkflowGenericResponse<WorkflowTemplateResponseData>,
      WorkflowTemplatePayload
    >(pathWorkflowTemplates, templateData);
  }

  /**
   * Applies an existing workflow template to a project, instantiating milestones.
   *
   * @param {WorkflowTemplateApplyPayload} applyData - Application data including templateId, projectId, anchorDate, and optional overrides
   * @returns {Promise<WorkflowGenericResponse<WorkflowTemplateApplyResponseData>>} Created instance with milestones and warnings
   * @throws {Error} If the API request fails
   */
  static async applyWorkflowTemplate(
    applyData: WorkflowTemplateApplyPayload
  ): Promise<WorkflowGenericResponse<WorkflowTemplateApplyResponseData>> {
    return http.post<
      WorkflowGenericResponse<WorkflowTemplateApplyResponseData>,
      WorkflowTemplateApplyPayload
    >(pathWorkflowTemplatesApply, applyData);
  }

  /**
   * Updates a milestone's status, planned_date, actual_date, or notes.
   * Automatically sets status to 'completed' when actual_date is provided.
   * Captures audit trail for all field changes.
   *
   * @param {string} milestoneId - ID of the milestone to update
   * @param {WorkflowMilestoneUpdatePayload} updateData - Update data including status, plannedDate, actualDate, or notes
   * @returns {Promise<WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>>} Updated milestone with change summary
   * @throws {Error} If the API request fails
   */
  static async updateWorkflowMilestone(
    milestoneId: string,
    updateData: WorkflowMilestoneUpdatePayload
  ): Promise<WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>> {
    if (!milestoneId) {
      throw new Error('Milestone ID is required.');
    }
    return http.patch<
      WorkflowGenericResponse<WorkflowMilestoneUpdateResponseData>,
      WorkflowMilestoneUpdatePayload
    >(pathWorkflowMilestoneId(milestoneId), updateData);
  }

  /**
   * Retrieves aggregated schedule summaries for projects with optional filters and pagination.
   *
   * @param {ScheduleAggregateRequestParams} params - Optional filter and pagination configuration.
   * @returns {Promise<WorkflowGenericResponse<ScheduleAggregateResponseData>>} Aggregated schedule data.
   */
  static async getScheduleAggregates(
    params?: ScheduleAggregateRequestParams
  ): Promise<WorkflowGenericResponse<ScheduleAggregateResponseData>> {
    const queryParams = buildScheduleAggregateQuery(params);
    return http.get<
      WorkflowGenericResponse<ScheduleAggregateResponseData>
    >(pathWorkflowScheduleAggregates, {
      params: queryParams,
    });
  }

  /**
   * Triggers the overdue detection job for the current tenant (or a specific tenant if authorized).
   *
   * @param {Object} [options]
   * @param {string} [options.tenantId] - Optional tenant override (requires elevated permissions).
   * @returns {Promise<WorkflowGenericResponse<OverdueDetectionSummary>>} Job execution summary.
   */
  static async runOverdueDetectionJob(options?: {
    tenantId?: string;
  }): Promise<WorkflowGenericResponse<OverdueDetectionSummary>> {
    const config =
      options?.tenantId !== undefined
        ? { params: { tenantId: options.tenantId } }
        : undefined;

    return http.post<
      WorkflowGenericResponse<OverdueDetectionSummary>,
      void
    >(pathWorkflowOverdueDetectionJob, undefined, config);
  }
}

export default WorkflowTemplateService;

const buildScheduleAggregateQuery = (
  params?: ScheduleAggregateRequestParams
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const query: Record<string, string | number> = {};
  let hasEntries = false;

  const filters = params.filters;
  if (filters) {
    const serializeList = (values?: string[] | null) =>
      values && values.length ? values.join(',') : undefined;

    const statusValue = serializeList(filters.statuses);
    if (statusValue) {
      query.status = statusValue;
      hasEntries = true;
    }

    const disciplineValue = serializeList(filters.disciplines);
    if (disciplineValue) {
      query.discipline = disciplineValue;
      hasEntries = true;
    }

    const clientValue = serializeList(filters.clientIds);
    if (clientValue) {
      query.clientId = clientValue;
      hasEntries = true;
    }

    const projectTypeValue = serializeList(filters.projectTypes);
    if (projectTypeValue) {
      query.projectType = projectTypeValue;
      hasEntries = true;
    }

    const userValue = serializeList(filters.userIds);
    if (userValue) {
      query.userId = userValue;
      hasEntries = true;
    }

    if (filters.startDate) {
      query.startDate = filters.startDate;
      hasEntries = true;
    }

    if (filters.endDate) {
      query.endDate = filters.endDate;
      hasEntries = true;
    }
  }

  const pagination = params.pagination;
  if (pagination) {
    if (pagination.page != null) {
      query.page = pagination.page;
      hasEntries = true;
    }
    if (pagination.limit != null) {
      query.limit = pagination.limit;
      hasEntries = true;
    }
    if (pagination.sortBy) {
      query.sortBy = pagination.sortBy;
      hasEntries = true;
    }
    if (pagination.sortOrder) {
      query.sortOrder = pagination.sortOrder;
      hasEntries = true;
    }
  }

  return hasEntries ? query : undefined;
};

